import { getApiHost, logout } from '@/utils/auth';
import config from '@/utils/config';
import Helper from "@/utils/helper";
import * as Enums from '@/utils/enums';
import Storage from '@/utils/storage';
import { isSessionDenied } from '@/services/platform-auth';
import { refreshPlatformSession } from '@/services/platform-token-refresh';

async function request(url: any, params: any, method: any, toastCtx: any, czTenantID: any, czCustomerID: any, apiUrlOverride: any, caller: any, signal: any, statusIfNull: any, cacheMode?: RequestCache) {
  const options: any = {
    method,
    headers: getHeaders(),
    signal: signal // abort signal
  };
  if (cacheMode) options.cache = cacheMode;

  if (params) {
    if (method === 'GET') {
      url += '?' + Helper.objectToQueryString(params);
    } else {
      options.body = JSON.stringify(params);
    }
  }

  function getHeaders() {
    let path = window.location.href;

    caller = caller ? caller : "";

    if (czTenantID && !czCustomerID) {
      return {
        'cache': 'no-store',
        'tenantid': czTenantID,
        'Content-Type': 'application/json',
        'location': path,
        'caller': caller,
        'SC-Version': __APP_VERSION__
      };
    } else if (czTenantID && czCustomerID) {
      return {
        'cache': 'no-store',
        'tenantid': czTenantID,
        'customerid': czCustomerID,
        'Content-Type': 'application/json',
        'location': path,
        'caller': caller,
        'SC-Version': __APP_VERSION__
      };
    } else {
      const localToken = Storage.getCookie(Enums.Cookie.token);
      const localTenantID = Storage.getCookie(Enums.Cookie.tenantID);
      const localDeviceID = Storage.getCookie(Enums.Cookie.fingerPrint);
      const { token, tenantID, fingerPrint } = { token: localToken, tenantID: localTenantID, fingerPrint: localDeviceID };

      return {
        'cache': 'no-store',
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json',
        'tenantid': tenantID,
        'deviceid': fingerPrint,
        'location': path,
        'caller': caller,
        'SC-Version': __APP_VERSION__
      };
    }
  }

  let apiHost = getApiHost(apiUrlOverride);
  const reqToken = Storage.getCookie(Enums.Cookie.token);

  let response = await fetch(apiHost + url, options as any);

  if (response.status === 401) {
    const observedAccessToken = typeof window !== 'undefined'
      ? Storage.getCookie('platformAccessToken')
      : null;

    if (observedAccessToken) {
      // A 401 with a platform session means either the access token expired or the
      // session was revoked server-side (permission change, device eviction). Force a
      // refresh regardless of remaining token lifetime — only PlatformAuth can tell us
      // which it is — then retry the request once with the new token.
      //
      // Via refreshPlatformSession so that a burst of parallel 401s (or another tab's
      // heartbeat firing at the same moment) spends the single-use refresh token exactly
      // once. Replaying a consumed one makes PlatformAuth revoke every session the user
      // has, which shows up as an unexplained logout everywhere at once.
      let newTokens: any = null;
      try {
        newTokens = await refreshPlatformSession('unauthorized', observedAccessToken);
      } catch (refreshErr: any) {
        if (isSessionDenied(refreshErr)) {
          logout(true, 'Your access has changed — please log in again', 'refresh-denied-after-401');
        } else {
          console.warn('[AUTH] Token refresh failed on 401 — keeping session, will retry:', refreshErr);
        }
        return { ResponseStatus: 401, Results: [], Result: {}, TotalResults: 0 };
      }

      if (!newTokens) {
        // The session was cleared while we waited for the refresh lock — another tab
        // logged out. Nothing to retry with, and logout() has already been broadcast.
        return { ResponseStatus: 401, Results: [], Result: {}, TotalResults: 0 };
      }

      // Rebuild options with the refreshed token (getHeaders() reads storage at call-time,
      // and refreshPlatformSession has already written the new pair there)
      const retryOptions = { ...options, headers: getHeaders() };
      const retryResponse = await fetch(apiHost + url, retryOptions as any);

      if (retryResponse.status === 401) {
        // The refresh succeeded but the API still rejects the new token. Returning an
        // empty envelope here would leave the user on blank pages with a valid-looking
        // session — log out instead.
        console.warn('[AUTH] Retry after refresh still returned 401 — logging out');
        logout(true, 'Your access has changed — please log in again', 'retry-after-refresh-401');
        return { ResponseStatus: 401, Results: [], Result: {}, TotalResults: 0 };
      }

      // Fall through to the shared response processing below so the retried response
      // gets identical treatment (204 handling, statusIfNull, tolerant JSON parsing,
      // headerTenantName unwrapping, SubscriptionInfo persistence).
      response = retryResponse;
    } else {
      // TODO verify this
      const currentToken = Storage.getCookie(Enums.Cookie.token);
      if (!reqToken || currentToken !== reqToken) {
        // Token was absent or changed while this request was in-flight.
        // Calling logout() here would broadcast a stale logout timestamp and disrupt
        // a newer session (e.g. another tab that just logged in).
        return { ResponseStatus: response.status, Results: [], Result: {}, TotalResults: 0 };
      }
      let logoutMessage = await response.text();
      logout(true, logoutMessage, 'legacy-401');
      return { ResponseStatus: response.status, Results: [], Result: {}, TotalResults: 0 };
    }
  }

  if (response.status !== 200 && response.status !== 204) {

    let serverMessage: string | null = null;
    try {
      serverMessage = await response.text();
    } catch { }

    // Try to parse serverMessage if it contains JSON (common API error envelope)
    const parsed = Helper.parseString(serverMessage);

    if (toastCtx) {
      toastCtx.setToast({
        message: parsed?.Message
          ? parsed.Message
          : (serverMessage ? serverMessage : 'The server responded with an unexpected status. (' + response.status + ')'),
        show: true,
        type: 'error'
      })
    }

    const errObj: any = {
      status: response.status,
      message: 'The server responded with an unexpected status. (' + response.status + ')',
      serverMessage: serverMessage
    };

    // Promote known fields from parsed server error so callers can rely on them
    if (parsed && typeof parsed === 'object') {
      if (parsed.HttpStatusCode !== undefined) errObj.HttpStatusCode = parsed.HttpStatusCode;
      if (parsed.Message !== undefined) errObj.Message = parsed.Message;
      if (parsed.ResponseStatus !== undefined) errObj.ResponseStatus = parsed.ResponseStatus;
      if (parsed.TotalResults !== undefined) errObj.TotalResults = parsed.TotalResults;
      if (parsed.ReturnedResults !== undefined) errObj.ReturnedResults = parsed.ReturnedResults;
      if (parsed.Results !== undefined) errObj.Results = parsed.Results;
      if (parsed.Result !== undefined) errObj.Result = parsed.Result;
      if (parsed.Metadata !== undefined) errObj.Metadata = parsed.Metadata;
    }

    return errObj;
  }

  const data = response.status === 204 ? "" : await response.text();
  let json = Helper.parseString(data);
  if (statusIfNull && !!!json) {
    json = {
      ResponseStatus: response.status
    };
  }

  const keys = json ? Object.keys(json) : [];
  if (keys.includes("headerTenantName") && keys.includes("data")) {
    // need to clean up the response and append headerTenantName to the data object
    let newJson = json.data;
    if (!!newJson && typeof newJson === "object") newJson.headerTenantName = json.headerTenantName;
    json = newJson;
  }

  if (json && json.SubscriptionInfo !== undefined && json.SubscriptionInfo !== null) {
    Storage.setCookie(Enums.Cookie.subscriptionInfo, json.SubscriptionInfo);
  }

  return json;
}

interface RequestObject {
  url: string
  params?: any
  toastCtx?: any
  tenantID?: string
  customerID?: string
  apiUrlOverride?: string
  caller?: string
  signal?: any
  statusIfNull?: boolean
  cache?: RequestCache
}

function get({ url, params, toastCtx, tenantID, customerID, apiUrlOverride, caller, signal, statusIfNull = false, cache }: RequestObject) {
  return request(url, params, 'GET', toastCtx, tenantID, customerID, apiUrlOverride, caller, signal, statusIfNull, cache);
}

function post({ url, params, toastCtx, tenantID, customerID, apiUrlOverride, caller, signal, statusIfNull = false }: RequestObject) {
  return request(url, params, 'POST', toastCtx, tenantID, customerID, apiUrlOverride, caller, signal, statusIfNull);
}

function put({ url, params, toastCtx, tenantID, customerID, apiUrlOverride, caller, signal, statusIfNull = false }: RequestObject) {
  return request(url, params, 'PUT', toastCtx, tenantID, customerID, apiUrlOverride, caller, signal, statusIfNull);
}

function destroy({ url, params, toastCtx, tenantID, customerID, apiUrlOverride, caller, signal, statusIfNull = false }: RequestObject) {
  return request(url, params, 'DELETE', toastCtx, tenantID, customerID, apiUrlOverride, caller, signal, statusIfNull);
}

async function preLogin({ userName, password, tenantID, managerServCraftLoginCredentials, toastCtx }: any) {
  const opts: any = {
    method: "POST",
    body: JSON.stringify({
      userName,
      password,
      customerID: tenantID,
      managerServCraftLoginCredentials: managerServCraftLoginCredentials
    }),
    headers: {
      'Content-Type': 'application/json',
      'SC-Version': __APP_VERSION__
    }
  };

  const response = await fetch(config.managerHost + "/Authentication/PreLogin", opts);

  if (response.status === 401) {
    logout(true, '', 'prelogin-401');
  }

  if (response.status !== 200) {
    if (toastCtx) {
      toastCtx.setToast({
        message: 'The server responded with an unexpected status. (' + response.status + ')',
        show: true,
        type: 'error'
      })
    }

    return {
      status: response.status,
      message: 'The server responded with an unexpected status. (' + response.status + ')'
    };
  }

  const data = await response.text();
  const json = (data.length ? JSON.parse(data) : { ResponseStatus: response.status });

  return json;
}

export default {
  get,
  post,
  put,
  destroy,
  preLogin
};
